import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Zap, Clock, Eye, Terminal } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatCurrency } from '@/lib/currency'

type Tier = 'speed' | 'standard' | 'premium' | 'media'

interface ModelCardProps {
  modelId: string
  name: string
  tier: Tier
  description: string
  inputCostUSD: number
  outputCostUSD: number
  contextWindow: number
  speedMs?: number
  supportsVision?: boolean
  currency?: string
}

const tierLabels: Record<Tier, string> = {
  speed: 'Speed',
  standard: 'Standard',
  premium: 'Premium',
  media: 'Media',
}

export function ModelCard({
  modelId,
  name,
  tier,
  description,
  inputCostUSD,
  outputCostUSD,
  contextWindow,
  speedMs,
  supportsVision = false,
  currency = 'PHP',
}: ModelCardProps) {
  const navigate = useNavigate()

  function formatContextWindow(tokens: number): string {
    if (tokens >= 1_000_000) return `${(tokens / 1_000_000).toFixed(1)}M ctx`
    if (tokens >= 1_000) return `${(tokens / 1_000).toFixed(0)}K ctx`
    return `${tokens} ctx`
  }

  function formatSpeed(ms?: number): string {
    if (!ms) return '—'
    if (ms >= 1000) return `~${(ms / 1000).toFixed(1)}s`
    return `~${ms}ms`
  }

  const handlePlayground = () => {
    navigate(`/dashboard/playground?model=${modelId}`)
  }

  return (
    <Card className="card-hover flex flex-col">
      <CardContent className="p-5 flex flex-col gap-3 h-full">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-semibold text-gray-900 text-sm">{name}</h3>
            <p className="text-xs text-gray-400 font-mono mt-0.5">{modelId}</p>
          </div>
          <Badge variant={tier}>{tierLabels[tier]}</Badge>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-500 leading-relaxed flex-1">{description}</p>

        {/* Stats */}
        <div className="flex flex-wrap gap-2">
          {speedMs && (
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Zap className="h-3 w-3 text-yellow-500" />
              {formatSpeed(speedMs)}
            </div>
          )}
          {contextWindow > 0 && (
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Clock className="h-3 w-3 text-gray-400" />
              {formatContextWindow(contextWindow)}
            </div>
          )}
          {supportsVision && (
            <div className="flex items-center gap-1 text-xs text-indigo-600">
              <Eye className="h-3 w-3" />
              Vision
            </div>
          )}
        </div>

        {/* Pricing */}
        {inputCostUSD > 0 && (
          <div className="border-t border-gray-100 pt-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-xs text-gray-400">Input / 1M tokens</p>
                <p className="text-sm font-semibold text-gray-800">
                  {formatCurrency(inputCostUSD, currency)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Output / 1M tokens</p>
                <p className="text-sm font-semibold text-gray-800">
                  {formatCurrency(outputCostUSD, currency)}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <Button
          variant="outline"
          size="sm"
          className="w-full mt-auto"
          onClick={handlePlayground}
        >
          <Terminal className="h-3.5 w-3.5" />
          Try in Playground
        </Button>
      </CardContent>
    </Card>
  )
}
